import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Cpu, RefreshCw, CheckCircle2 } from 'lucide-react';
import api from '@/api';
import { useReviewData } from '@/hooks/useReviewData';
import { useEditorActions } from '@/hooks/useEditorActions';
import DocumentViewer from '@/components/review/DocumentViewer';
import EditableSpacesPanel from '@/components/EditableSpacesPanel';
import AssetsPanel from '@/components/review/AssetsPanel';

export default function ReviewPage() {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const deepLinkSheet = searchParams.get('sheet');
  const highlightTag = searchParams.get('tag');
  const didDeepLink = useRef(false);
  const data = useReviewData(jobId);
  const actions = useEditorActions(jobId, data.selectedSheet, {
    fetchJob: data.fetchJob,
    fetchSheetDetails: data.fetchSheetDetails,
  });

  const [zoom, setZoom] = useState(0.5);
  const [editorMode, setEditorMode] = useState('select');
  const [activeDataTab, setActiveDataTab] = useState('spaces');
  const [selectedPolygonId, setSelectedPolygonId] = useState(null);
  const [selectedSpaceId, setSelectedSpaceId] = useState(null);
  const [highlightedPolygonIds, setHighlightedPolygonIds] = useState([]);
  const [scrollToPolygon, setScrollToPolygon] = useState(null);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [confidenceThreshold, setConfidenceThreshold] = useState(0);

  // Resizable right panel
  const [panelWidth, setPanelWidth] = useState(420);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  // Deep-link from the register: ?sheet=P-04&tag=P3 → select that sheet once.
  useEffect(() => {
    if (didDeepLink.current || !deepLinkSheet || !data.sheets?.length) return;
    const match = data.sheets.find(s => s.sheet_number === deepLinkSheet);
    if (match) {
      data.setSelectedSheet(match);
      didDeepLink.current = true;
    }
  }, [deepLinkSheet, data.sheets, data.setSelectedSheet]);


  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      const delta = startX.current - e.clientX;
      const newWidth = Math.min(Math.max(startWidth.current + delta, 260), 900);
      setPanelWidth(newWidth);
    };
    const onMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleDividerMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    startX.current = e.clientX;
    startWidth.current = panelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [panelWidth]);

  const roomPolygons = data.sheetDetails?.room_polygons || [];
  const sheetSpaces = (data.sheetDetails?.spaces?.length > 0)
    ? data.sheetDetails.spaces
    : (data.job?.spaces || []);
  const sheetAssets = (data.sheetDetails?.assets?.length > 0)
    ? data.sheetDetails.assets
    : (data.job?.asset_candidates || []);

  // Confidence-based counts
  const pendingSpaces = sheetSpaces.filter(s => s.status === 'pending');
  const pendingAboveCount = confidenceThreshold > 0
    ? pendingSpaces.filter(s => (s.confidence || 0) >= confidenceThreshold).length
    : 0;
  const pendingBelowCount = confidenceThreshold > 0
    ? pendingSpaces.filter(s => (s.confidence || 0) > 0 && (s.confidence || 0) < confidenceThreshold).length
    : 0;

  const handleBulkAccept = useCallback(async () => {
    if (!data.selectedSheet) return;
    try {
      const res = await api.post(
        `/drawing-jobs/${jobId}/sheets/${data.selectedSheet.id}/bulk-accept`,
        { threshold: confidenceThreshold }
      );
      if (res.data.accepted > 0) {
        const { toast } = await import('sonner');
        toast.success(`Accepted ${res.data.accepted} spaces above ${Math.round(confidenceThreshold * 100)}%`);
        data.fetchSheetDetails();
      }
    } catch {}
  }, [jobId, data.selectedSheet, confidenceThreshold, data.fetchSheetDetails]);

  const [approvingAll, setApprovingAll] = useState(false);
  const handleApproveAllSheet = useCallback(async () => {
    const isSpaces = activeDataTab === 'spaces';
    const items = isSpaces ? sheetSpaces : sheetAssets;
    const entityType = isSpaces ? 'space' : 'asset_candidate';
    const pending = items.filter(i => (i.status || 'pending') === 'pending');
    if (pending.length === 0) return;
    setApprovingAll(true);
    try {
      await api.post(`/drawing-jobs/${jobId}/review`, {
        decisions: pending.map(i => ({ entity_type: entityType, entity_id: i.id, action: 'approved' })),
      });
      const { toast } = await import('sonner');
      toast.success(`Approved ${pending.length} ${isSpaces ? 'spaces' : 'assets'} on this sheet`);
      await Promise.all([data.fetchJob(), data.fetchSheetDetails()]);
    } catch {
      const { toast } = await import('sonner');
      toast.error('Approve all failed');
    } finally { setApprovingAll(false); }
  }, [jobId, activeDataTab, sheetSpaces, sheetAssets, data.fetchJob, data.fetchSheetDetails]);

  const normalizeRoomNum = (num) => (num || '').toString().replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  const numericSuffix = (num) => (num || '').toString().replace(/[^0-9]/g, '');

  // Keep a ref to latest data so polygon/space click handlers never stale
  const latestRef = useRef({ spaces: [], polygons: [] });
  latestRef.current = {
    spaces: sheetSpaces,
    polygons: roomPolygons,
  };

  // Match a room_number against polygon list, trying exact then numeric-suffix fallback
  const findMatchingPolygons = useCallback((roomNum, polygons) => {
    if (!roomNum) return [];
    const norm = normalizeRoomNum(roomNum);
    if (!norm) return [];
    // 1. Exact normalized match
    let matched = polygons.filter(p => p.room_number && normalizeRoomNum(p.room_number) === norm);
    if (matched.length > 0) return matched;
    // 2. Numeric-suffix match (e.g., X260 ↔ N260 via "260")
    const suffix = numericSuffix(roomNum);
    if (suffix && suffix.length >= 2) {
      matched = polygons.filter(p => p.room_number && numericSuffix(p.room_number) === suffix);
    }
    return matched;
  }, []);

  const handlePolygonClick = useCallback((polygon) => {
    setSelectedPolygonId(polygon.id);
    setHighlightedPolygonIds([polygon.id]);
    setActiveDataTab('spaces');

    const { spaces } = latestRef.current;
    // 1. Match by linked_polygon_id
    let matched = spaces.find(s => s.linked_polygon_id === polygon.id);
    // 2. Exact room_number match only (no fuzzy/suffix — avoids false scrolls)
    if (!matched && polygon.room_number) {
      const norm = normalizeRoomNum(polygon.room_number);
      if (norm) matched = spaces.find(s => normalizeRoomNum(s.room_number) === norm);
    }
    if (matched) {
      setSelectedSpaceId(matched.id);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="review-space-${matched.id}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('animate-pulse');
          setTimeout(() => el.classList.remove('animate-pulse'), 1500);
        }
      }, 150);
    } else {
      // No matching space — auto-create one linked to this polygon
      setSelectedSpaceId(null);
      const polyId = polygon.id;
      actions.handleCreateSpace({
        room_number: polygon.room_number || `P-${polygon.id.slice(0, 6).toUpperCase()}`,
        room_name: '',
        linked_polygon_id: polygon.id,
        area: polygon.area_sqft || null,
      }).then(() => {
        // After creation, find the newly created space by its linked_polygon_id
        setTimeout(() => {
          const { spaces: freshSpaces } = latestRef.current;
          const newSpace = freshSpaces.find(s => s.linked_polygon_id === polyId);
          if (newSpace) {
            setSelectedSpaceId(newSpace.id);
            const el = document.querySelector(`[data-testid="review-space-${newSpace.id}"]`);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el.classList.add('animate-pulse');
              setTimeout(() => el.classList.remove('animate-pulse'), 1500);
            }
          }
        }, 500);
      });
    }
  }, [actions]);

  const handleSpaceClick = useCallback((space) => {
    setSelectedSpaceId(space.id);

    const { polygons } = latestRef.current;
    let matched = [];
    if (space.linked_polygon_id) {
      const poly = polygons.find(p => p.id === space.linked_polygon_id);
      if (poly) matched = [poly];
    }
    if (matched.length === 0 && space.room_number) {
      matched = findMatchingPolygons(space.room_number, polygons);
    }
    if (matched.length > 0) {
      setHighlightedPolygonIds(matched.map(p => p.id));
      setSelectedPolygonId(matched[0].id);
      if (matched[0].centroid_pct) {
        setScrollToPolygon({ x: matched[0].centroid_pct.x, y: matched[0].centroid_pct.y });
      }
    } else {
      setHighlightedPolygonIds([]);
      setSelectedPolygonId(null);
    }
  }, [findMatchingPolygons]);

  const handleDeletePolygon = useCallback(async (polygonId) => {
    setSelectedPolygonId(null);
    await actions.handleDeletePolygon(polygonId);
  }, [actions]);

  const handleDeleteSpace = useCallback(async (spaceId) => {
    setSelectedSpaceId(null);
    await actions.handleDeleteSpace(spaceId);
  }, [actions]);

  // Clear selections on sheet change
  useEffect(() => {
    setSelectedPolygonId(null);
    setSelectedSpaceId(null);
    setHighlightedPolygonIds([]);
    setScrollToPolygon(null);
  }, [data.selectedSheet?.id]);

  // Keyboard shortcuts for tools
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const keyMap = { v: 'select', h: 'pan', d: 'draw', s: 'seed', e: 'edit', x: 'delete' };
      const mode = keyMap[e.key.toLowerCase()];
      if (mode) setEditorMode(mode);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  if (data.loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  if (!data.job) {
    return <div className="p-10 text-muted-foreground">Job not found</div>;
  }

  return (
    <div className="h-[calc(100vh)] flex flex-col" data-testid="review-page">
      {/* Top Bar */}
      <div className="h-12 bg-card border-b border-border flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-3">
          <Link to={`/jobs/${jobId}`} className="p-1.5 hover:bg-secondary rounded text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-job">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <span className="font-heading font-bold text-xs uppercase tracking-wider text-foreground/80">
            Review: {data.job.project_name}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!data.sheetDetails?.processed && data.selectedSheet && (
            <button
              onClick={actions.handleProcessDocuments}
              disabled={actions.processingDocs}
              data-testid="process-docs-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider bg-violet-600/20 text-violet-400 border border-violet-800 rounded-sm hover:bg-violet-600/30 transition-colors disabled:opacity-50"
            >
              <Cpu className="w-3 h-3" />
              {actions.processingDocs ? 'Processing...' : 'Process Document'}
            </button>
          )}
          <button
            onClick={() => actions.handleDownload(data.imageVersion)}
            data-testid="download-sheet-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider text-muted-foreground border border-border rounded-sm hover:bg-secondary transition-colors"
          >
            <Download className="w-3 h-3" />
            Export
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Document Viewer */}
        <DocumentViewer
          sheets={data.sheets}
          selectedSheet={data.selectedSheet}
          onSelectSheet={data.setSelectedSheet}
          sheetDetails={data.sheetDetails}
          highlightTag={highlightTag}
          sheetImage={data.sheetImage}
          imageLoading={data.imageLoading}
          imageVersion={data.imageVersion}
          onImageVersionChange={data.setImageVersion}
          zoom={zoom}
          onZoomChange={setZoom}
          editorMode={editorMode}
          onModeChange={setEditorMode}
          selectedPolygonId={selectedPolygonId}
          highlightedPolygonIds={highlightedPolygonIds}
          onPolygonClick={handlePolygonClick}
          onPolygonHover={() => {}}
          onCreatePolygon={actions.handleCreatePolygon}
          onUpdatePolygon={actions.handleUpdatePolygon}
          onDeletePolygon={handleDeletePolygon}
          onSeedDetect={actions.handleSeedDetect}
          onPublish={actions.handlePublish}
          onAutoLabel={actions.handleAutoLabel}
          onCancelDraw={() => setEditorMode('select')}
          onProcessDocuments={actions.handleProcessDocuments}
          spacesCount={sheetSpaces.length}
          spaces={sheetSpaces}
          canvasLoading={actions.canvasLoading}
          autoLabeling={actions.autoLabeling}
          processingDocs={actions.processingDocs}
          labelProgress={actions.labelProgress}
          scrollToPolygon={scrollToPolygon}
          onCleanupSpaces={actions.handleCleanupSpaces}
          onClassifySpaceTypes={actions.handleClassifySpaceTypes}
          snapEnabled={snapEnabled}
          onSnapToggle={() => setSnapEnabled(prev => !prev)}
          confidenceThreshold={confidenceThreshold}
          onConfidenceChange={setConfidenceThreshold}
          onBulkAccept={handleBulkAccept}
          pendingBelowCount={pendingBelowCount}
          pendingAboveCount={pendingAboveCount}
          onDetectOverlaps={actions.handleDetectOverlaps}
        />

        {/* Draggable Divider */}
        <div
          data-testid="panel-resizer"
          onMouseDown={handleDividerMouseDown}
          className="w-1.5 shrink-0 cursor-col-resize bg-secondary hover:bg-primary/60 active:bg-primary transition-colors relative group"
        >
          <div className="absolute inset-y-0 -left-1 -right-1" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-0.5 h-8 bg-slate-600 group-hover:bg-amber-400 rounded-full transition-colors" />
        </div>

        {/* Right Panel - Extracted Data */}
        <div style={{ width: panelWidth }} className="bg-card flex flex-col shrink-0" data-testid="data-panel">
          <div className="h-10 border-b border-border flex items-center px-4">
            <span className="font-heading font-bold text-xs uppercase tracking-wider text-muted-foreground">
              Extracted Data
            </span>
            {data.selectedSheet && (
              <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                {data.selectedSheet.sheet_number} / {data.selectedSheet.discipline}
              </span>
            )}
          </div>

          <div className="flex border-b border-border px-2">
            {[
              { key: 'spaces', label: 'Spaces', count: sheetSpaces.length },
              { key: 'assets', label: 'Assets', count: sheetAssets.length },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveDataTab(tab.key)}
                data-testid={`data-tab-${tab.key}`}
                className={`px-3 py-2 font-heading text-[10px] uppercase tracking-wider transition-colors border-b-2 -mb-px ${
                  activeDataTab === tab.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground/80'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {(() => {
            const isSpaces = activeDataTab === 'spaces';
            const items = isSpaces ? sheetSpaces : sheetAssets;
            const pendingCount = items.filter(i => (i.status || 'pending') === 'pending').length;
            if (pendingCount === 0) return null;
            return (
              <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-secondary/30">
                <span className="font-mono text-[10px] text-muted-foreground">{pendingCount} pending</span>
                <button onClick={handleApproveAllSheet} disabled={approvingAll}
                  data-testid="approve-all-btn"
                  className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-heading uppercase tracking-wider rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-50">
                  <CheckCircle2 className="w-3 h-3" />
                  {approvingAll ? 'Approving…' : `Approve all (${pendingCount})`}
                </button>
              </div>
            );
          })()}

          <div className="flex-1 overflow-auto" data-testid="data-list">
            {activeDataTab === 'spaces' && (
              <EditableSpacesPanel
                spaces={sheetSpaces}
                selectedSpaceId={selectedSpaceId}
                onSpaceClick={handleSpaceClick}
                onUpdateSpace={actions.handleUpdateSpace}
                onCreateSpace={actions.handleCreateSpace}
                onDeleteSpace={handleDeleteSpace}
                onReview={actions.handleReview}
              />
            )}
            {activeDataTab === 'assets' && (
              <AssetsPanel
                assets={sheetAssets}
                onReview={actions.handleReview}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
